import { z } from 'zod'
import { listSchema } from '../common'
import { zId, zIdNullable, zIsoDate } from '../common'
import { zLinkStatus } from '../enums'

// Conversion lifecycle as the sync jobs (Awin / TimeOne / Kwanko / Mobimatter)
// map it. `state` is a free string server-side; these are the values the UI
// knows how to tone, and the ones the state filter offers. Unknown values still
// render verbatim and fall back to a neutral tone (see conversionStateTone).
//   pending_approval → the network has not validated the sale yet.
//   validated        → approved; this is the ONLY state uploaded to Google Ads.
//   rejected         → the network declined it (no commission).
export const CONVERSION_STATES = ['pending_approval', 'validated', 'rejected'] as const
export type ConversionState = (typeof CONVERSION_STATES)[number]

// Nested affiliate_link on a conversion row. There is no Product catalog —
// `product` IS the link that earned the commission. `targetKey` is the display
// name (opaque; never parse it). Null only if the FK is dangling.
export const zConversionProduct = z.object({
  id: zId,
  targetKey: z.string(),
  targetUrl: z.string(),
  status: zLinkStatus,
  affiliatePartnerId: zId,
  affiliatePartnerName: z.string(),
  affiliatePartnerLabel: z.string().nullable(),
})
export type ConversionProduct = z.infer<typeof zConversionProduct>

// One conversion the back-office ledger shows: a network-reported commission
// joined to the site that earned it, plus the two loop-closing signals —
// `attributed` (did it carry a click_token, i.e. can it ever reach Google Ads?)
// and `googleUploadedAt` (has the validated sale been pushed as an offline
// conversion yet? null = still open).
export const zConversionRow = z.object({
  id: zId,
  // The affiliate_link's property. Present in practice, but nullable-safe: a
  // link with a since-deleted property must not make the whole list throw.
  propertyId: zIdNullable,
  propertySlug: z.string().nullable(),
  campaignId: z.union([z.string(), z.number()]).nullable(),
  // Free string; parsed loosely so a new network state never breaks the table.
  state: z.string(),
  conversionType: z.string().nullable(),
  // Commission in euros (JSON number), or null when the network omitted it.
  earnings: z.number().nullable(),
  currency: z.string().nullable(),
  // click_token present at ingestion → the sale can be tied back to its click.
  attributed: z.boolean(),
  completedAt: zIsoDate,
  // ISO-8601 once the offline-conversion upload job has run, else null.
  googleUploadedAt: zIsoDate.nullable(),
  createdAt: zIsoDate,
  product: zConversionProduct.nullable(),
})
export type ConversionRow = z.infer<typeof zConversionRow>

// GET /conversions → { conversions: [...], meta }. Paginated, newest first.
export const zConversionList = listSchema('conversions', zConversionRow)
export type ConversionList = z.infer<typeof zConversionList>
